import { useState, type ReactNode } from "react";
import { AlertTriangle, Loader2, RefreshCw, Share2 } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { useFetcher } from "~/hooks/useFetcher";
import { ConversationProvider } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { DownloadDialog } from "~/pages/manifold-nextgen/components/conversations/artifacts/DownloadDialog";
import {
  ArtifactLineagePanel,
  DownloadHeaderButton,
  LineageStatusButton,
  LineageStatusRow,
} from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/ArtifactLineagePanel";
import { RUN_STATUS_VARIANT } from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/LineageSteps";
import { isArtifactType, type ArtifactPreview, type ArtifactType } from "~/services/artifacts";
import {
  PinnedArtifact,
  StepState,
  isRefreshable,
  pinRef,
  pinnedArtifactPreviewPath,
} from "~/services/pinnedArtifacts";
import { useArtifactRefreshRun } from "./useArtifactRefreshRun";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

interface PinnedArtifactWorkflowProps {
  pin: PinnedArtifact;
  summary: string | null;
  artifactType: string;
  // Defaults to a download through the pin.
  headerActions?: ReactNode;
  // Unshares the artifact; omitted for a member who cannot curate.
  onUnshare?: () => void;
  // Where the artifact was shared from, as a link where the reader can open it.
  source?: ReactNode;
}

/** The lineage panel for a shared artifact: who shared it, the controls to
 *  run its workflow again, and the workflow itself.
 *
 *  The graph and steps come from the pin's frozen recipe rather than live
 *  lineage, because the recipe is what a refresh actually runs.
 */
export const PinnedArtifactWorkflow = ({
  pin,
  summary,
  artifactType,
  headerActions,
  onUnshare,
  source,
}: PinnedArtifactWorkflowProps) => {
  const { run, isStarting, start } = useArtifactRefreshRun(pin.project_id, pin.id, {
    activeRunId: pin.active_run_id,
    lastRunId: pin.last_refresh_run_id,
  });
  // Through the pin, so the thumbnail follows a refresh like the pin's card.
  const { data: preview } = useFetcher<ArtifactPreview>(
    pinnedArtifactPreviewPath(pin.project_id, pin.id)
  );
  const refreshable = isRefreshable(pin);
  const stateByStep = new Map<string, StepState>(
    (run?.step_states ?? []).map((state) => [state.step_id, state])
  );
  const isRunning = run?.status === "pending" || run?.status === "running";
  const sharedLine = `Shared by ${pin.created_by} · ${shortDate(pin.created_at)}`;
  const refreshedLine =
    run?.status === "succeeded" && run.finished_at
      ? `Last refreshed ${shortDate(run.finished_at)}`
      : `Not refreshed since it was shared`;

  return (
    // The pinned artifact may belong to another member's session, so no
    // conversation is in scope for its preview; the pin is, as on its card.
    <ConversationProvider conversationId={null} pin={pinRef(pin)}>
      <ArtifactLineagePanel
        name={pin.display_name}
        summary={summary}
        artifactType={artifactType}
        preview={preview}
        headerActions={
          headerActions ??
          (isArtifactType(artifactType) && (
            <PinDownload artifactId={preview?.artifact_id} artifactType={artifactType} />
          ))
        }
        statusLine={sharedLine}
        recipe={pin.recipe}
        isLoading={false}
        stateByStep={stateByStep}
        status={
          <>
            <LineageStatusRow
              action={
                onUnshare && (
                  <LineageStatusButton onClick={onUnshare}>
                    <Share2 className="size-3.5" aria-hidden />
                    Unshare
                  </LineageStatusButton>
                )
              }
              title={sharedLine}
              detail={source && <>From {source}</>}
            />
            <LineageStatusRow
              action={
                <LineageStatusButton
                  onClick={() => void start()}
                  disabled={!refreshable || isStarting || isRunning}
                >
                  {isStarting || isRunning ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="size-3.5" aria-hidden />
                  )}
                  {isRunning ? "Refreshing…" : "Refresh"}
                </LineageStatusButton>
              }
              title={
                <span className="flex items-center gap-2">
                  {refreshedLine}
                  {run && <Badge variant={RUN_STATUS_VARIANT[run.status]}>{run.status}</Badge>}
                </span>
              }
              detail="Reruns every lineage step on the latest data"
            />
            {!refreshable && (
              <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                This artifact can&apos;t be rebuilt: it wasn&apos;t derived from a dataset, so there
                is no query or code to run again.
              </p>
            )}
            {run?.error && <p className="text-xs text-destructive">{run.error}</p>}
          </>
        }
      />
    </ConversationProvider>
  );
};

// Downloads through the pin in scope. The pin's routes need no artifact id,
// so this does not wait for the preview; the id only attributes analytics.
const PinDownload = ({
  artifactId,
  artifactType,
}: {
  artifactId: string | undefined;
  artifactType: ArtifactType;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <DownloadHeaderButton onClick={() => setOpen(true)} />
      {open && (
        <DownloadDialog
          open
          onClose={() => setOpen(false)}
          artifactId={artifactId}
          artifactType={artifactType}
        />
      )}
    </>
  );
};
